import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BeforeAfterGallery = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [showBefore, setShowBefore] = useState(true);

  const projects = [
  {
    id: 1,
    title: "Electrical Panel Upgrade",
    category: "Residential",
    location: "Johannesburg",
    before: "https://images.unsplash.com/photo-1659474122276-aa162e37367a",
    beforeAlt: "Old outdated electrical panel with exposed wiring and rust showing signs of age and safety concerns",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1a56357-1766961752370.png",
    afterAlt: "Modern electrical panel with organized circuit breakers proper labeling and professional installation",
    problem: "Outdated 60-amp panel with insufficient capacity for modern appliances and safety concerns",
    solution: "Upgraded to 200-amp panel with modern circuit breakers, proper grounding, and organized wiring",
    duration: "1 day",
    features: ["200-amp capacity", "GFCI protection", "Surge protection", "Proper labeling"]
  },
  {
    id: 2,
    title: "Commercial Kitchen Wiring",
    category: "Commercial",
    location: "Pretoria",
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_15f721e92-1768148917029.png",
    beforeAlt: "Cluttered commercial kitchen with exposed temporary wiring and overloaded circuits creating safety hazards",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_1789bf5e3-1767642859655.png",
    afterAlt: "Professional commercial kitchen with dedicated circuits organized wiring and proper electrical infrastructure",
    problem: "Overloaded circuits causing frequent trips and inability to run all kitchen equipment simultaneously",
    solution: "Installed dedicated circuits for each major appliance with proper capacity and safety features",
    duration: "2 days",
    features: ["Dedicated circuits", "Three-phase power", "Emergency shutoff", "Code compliant"]
  },
  {
    id: 3,
    title: "Home Lighting Renovation",
    category: "Residential",
    location: "Cape Town",
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_151cd7547-1767201775156.png",
    beforeAlt: "Dark room with old fluorescent lighting fixtures and outdated switches showing poor illumination",
    after: "https://images.unsplash.com/photo-1647011943268-17ae8f8969d1",
    afterAlt: "Bright modern room with LED recessed lighting smart switches and ambient lighting creating welcoming atmosphere",
    problem: "Outdated fluorescent lighting with poor illumination and high energy consumption",
    solution: "Complete lighting redesign with LED fixtures, dimmer switches, and smart home integration",
    duration: "3 days",
    features: ["LED lighting", "Smart switches", "Dimmer controls", "Energy efficient"]
  },
  {
    id: 4,
    title: "Outlet Safety Upgrade",
    category: "Residential",
    location: "Durban",
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4952a57-1768148913045.png",
    beforeAlt: "Old two-prong outlets without grounding showing discoloration and potential safety hazards on wall",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_1680a7915-1768148914010.png",
    afterAlt: "Modern grounded outlets with GFCI protection and USB charging ports installed professionally on clean wall",
    problem: "Ungrounded two-prong outlets throughout home posing safety risks and limiting modern device use",
    solution: "Replaced all outlets with grounded GFCI-protected outlets and added USB charging capabilities",
    duration: "1 day",
    features: ["GFCI protection", "Grounded outlets", "USB charging", "Tamper resistant"]
  },
  {
    id: 5,
    title: "Emergency Repair - Fire Damage",
    category: "Emergency",
    location: "Johannesburg",
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_147757b96-1767079609479.png",
    beforeAlt: "Electrical fire damage showing burned wiring melted outlets and charred wall requiring urgent repair",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_14efa67cb-1767988526240.png",
    afterAlt: "Fully restored electrical system with new wiring modern outlets and repaired wall showing professional restoration",
    problem: "Electrical fire caused by faulty wiring requiring complete circuit replacement and safety inspection",
    solution: "Emergency response with complete rewiring, safety inspection, and restoration to code compliance",
    duration: "2 days",
    features: ["Emergency response", "Complete rewiring", "Safety inspection", "COC issued"]
  },
  {
    id: 6,
    title: "Retail Store Electrical System",
    category: "Commercial",
    location: "Pretoria",
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd0e221c-1768148913155.png",
    beforeAlt: "Retail space with inadequate lighting exposed wiring and insufficient power outlets for business needs",
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_186c85d85-1766589993527.png",
    afterAlt: "Modern retail space with professional track lighting adequate power distribution and clean electrical installation",
    problem: "Insufficient electrical capacity and poor lighting affecting business operations and customer experience",
    solution: "Complete electrical system upgrade with enhanced lighting, additional circuits, and modern infrastructure",
    duration: "3 days",
    features: ["Track lighting", "Multiple circuits", "Display lighting", "Emergency backup"]
  }];


  const currentProject = projects?.[activeProject];

  const handleNext = () => {
    setActiveProject((prev) => (prev + 1) % projects?.length);
    setShowBefore(true);
  };

  const handlePrev = () => {
    setActiveProject((prev) => (prev - 1 + projects?.length) % projects?.length);
    setShowBefore(true);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-4">
            Before & After Gallery
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            See the transformation. Real projects showcasing our commitment to quality workmanship and electrical safety excellence.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-card shadow-elevated">
              <Image
                src={showBefore ? currentProject?.before : currentProject?.after}
                alt={showBefore ? currentProject?.beforeAlt : currentProject?.afterAlt}
                className="w-full h-full object-cover" />

              <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="font-accent text-sm font-semibold text-foreground">
                  {showBefore ? 'BEFORE' : 'AFTER'}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={handlePrev}
                className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                aria-label="Previous project">

                <Icon name="ChevronLeft" size={24} />
              </button>

              <button
                onClick={() => setShowBefore(!showBefore)}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-accent hover:bg-primary/90 transition-colors">

                <Icon name={showBefore ? "Eye" : "EyeOff"} size={20} />
                <span>{showBefore ? 'Show After' : 'Show Before'}</span>
              </button>

              <button
                onClick={handleNext}
                className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                aria-label="Next project">

                <Icon name="ChevronRight" size={24} />
              </button>
            </div>

            <div className="flex justify-center gap-2 mt-4">
              {projects?.map((_, index) =>
              <button
                key={index}
                onClick={() => {
                  setActiveProject(index);
                  setShowBefore(true);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === activeProject ? 'bg-primary w-8' : 'bg-muted-foreground/30'}`
                }
                aria-label={`Go to project ${index + 1}`} />

              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-headline font-bold text-xl md:text-2xl text-foreground mb-2">
                  {currentProject?.title}
                </h3>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Tag" size={14} />
                    {currentProject?.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="MapPin" size={14} />
                    {currentProject?.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {currentProject?.duration}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                  <h4 className="font-headline font-semibold text-base text-foreground">The Problem</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground pl-7">
                  {currentProject?.problem}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                  <h4 className="font-headline font-semibold text-base text-foreground">The Solution</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground pl-7">
                  {currentProject?.solution}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Sparkles" size={20} color="var(--color-accent)" />
                  <h4 className="font-headline font-semibold text-base text-foreground">Key Features</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 pl-7">
                  {currentProject?.features?.map((feature, index) =>
                  <div key={index} className="flex items-center gap-2">
                      <Icon name="Check" size={14} color="var(--color-success)" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-body text-sm text-muted-foreground mb-1">
                    Project {activeProject + 1} of {projects?.length}
                  </p>
                  <p className="font-accent text-xs text-muted-foreground">
                    All work certified and guaranteed
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-lg">
                  <Icon name="BadgeCheck" size={18} />
                  <span className="font-accent text-sm">Completed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default BeforeAfterGallery;